import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Banknote, ShieldAlert, Sparkles, Loader2, RefreshCw, AlertTriangle,
  ChevronRight, Activity, TrendingDown, Wallet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const bandStyle = (band) => ({
  high:   'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-cn-sage-100/60 text-cn-primary border-cn-sage-200',
}[band] || 'bg-cn-bone-100 text-cn-text-muted border-cn-border');

const Gauge = ({ probability }) => {
  const pct = Math.round((probability || 0) * 100);
  const color = pct >= 50 ? '#ef4444' : pct >= 20 ? '#f59e0b' : '#0E3D3A';
  return (
    <div className="flex items-center gap-3" data-testid="denial-gauge">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 36 36" className="w-20 h-20">
          <path
            d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
            fill="none" stroke="#e5e7eb" strokeWidth="3"
          />
          <path
            d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
            fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${pct}, 100`} strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display font-bold text-lg" style={{ color }}>
          {pct}%
        </span>
      </div>
      <div className="text-xs text-cn-text-muted">
        Probability of denial<br />if submitted as-is
      </div>
    </div>
  );
};

export default function DenialPrediction() {
  const { getAuthHeader } = useAuth();
  const [history, setHistory] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const navItems = [
    { icon: Activity, label: 'Billing & RCM', href: '/clinician/billing' },
    { icon: ShieldAlert, label: 'Denial Prediction', active: true },
    { icon: Wallet, label: 'Earnings & Payouts', href: '/clinician/payouts' },
    { icon: Banknote, label: 'Operations · Executive', href: '/operations/executive' },
  ];

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/rcm/denial-history`, getAuthHeader());
      setHistory(data);
    } catch (e) {
      toast.error('Could not load denial history.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runBatch = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/rcm/predict-denial/batch`, {}, { ...getAuthHeader(), timeout: 30000 });
      setBatch(data);
      if (data.count === 0) {
        toast.info('No draft insurance claims to scan.');
      } else {
        toast.success(`Scanned ${data.count} draft claim${data.count === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      toast.error('Could not scan draft claims.');
    } finally {
      setScanning(false);
    }
  };

  const openDetail = async (claimId) => {
    setActiveClaim(claimId);
    setActiveDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.post(`${API}/rcm/claims/${claimId}/predict-denial`, {}, { ...getAuthHeader(), timeout: 90000 });
      setActiveDetail(data);
    } catch (e) {
      toast.error('Could not load denial prediction.');
      setActiveClaim(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const denialRatePct = Math.round((history?.denial_rate || 0) * 100);

  return (
    <DashboardShell
      navItems={navItems}
      title="Denial Prediction"
      subtitle="Pre-submission probability of denial + the specific edits to fix it."
      testId="denial-prediction-page"
      rightSlot={
        <Button
          onClick={runBatch}
          disabled={scanning}
          className="cn-btn cn-btn-primary !py-2.5"
          data-testid="denial-batch-scan-btn"
        >
          {scanning
            ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Scanning…</>
            : <><Sparkles className="w-4 h-4 mr-1" /> Scan all draft claims</>}
        </Button>
      }
    >
      {loading ? (
        <div className="grid sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="cn-card h-28 animate-pulse bg-cn-bg/60" />)}
        </div>
      ) : (
        <>
          {/* 90-day denial KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="denial-history-kpis">
            <div className="cn-card p-5" data-testid="denial-rate-tile">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">90-day denial rate</span>
                <TrendingDown className="w-4 h-4 text-cn-terracotta-600" />
              </div>
              <div className="mt-2 font-display font-bold text-2xl text-cn-text dark:text-white" data-testid="denial-rate-value">
                {denialRatePct}%
              </div>
              <div className="text-xs text-cn-text-muted mt-0.5">
                {history?.denied_claims ?? 0} of {history?.total_claims ?? 0} claims denied
              </div>
            </div>

            <div className="cn-card p-5" data-testid="top-denial-reasons-tile">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Top denial reasons</span>
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
              {history?.top_denial_reasons?.length ? (
                <ul className="space-y-1">
                  {history.top_denial_reasons.slice(0, 3).map((r, i) => (
                    <li key={i} className="text-xs flex justify-between gap-2">
                      <span className="text-cn-text truncate">{r.reason}</span>
                      <span className="font-bold text-cn-terracotta-600">{r.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-cn-text-muted">No denials yet — clean slate.</p>
              )}
            </div>

            <div className="cn-card p-5" data-testid="payer-breakdown-tile">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">By payer</span>
                <Activity className="w-4 h-4 text-cn-primary" />
              </div>
              {history?.by_payer?.length ? (
                <ul className="space-y-1">
                  {history.by_payer.slice(0, 3).map((p, i) => (
                    <li key={i} className="text-xs flex justify-between gap-2">
                      <span className="text-cn-text truncate">{p.payer}</span>
                      <span className="font-bold">{Math.round((p.denial_rate || 0) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-cn-text-muted">Need more claim history.</p>
              )}
            </div>
          </div>

          {/* Batch scan results */}
          {batch && (
            <div className="cn-card p-6 mt-6" data-testid="denial-batch-results">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-cn-primary" />
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">
                  {batch.count} draft claim{batch.count === 1 ? '' : 's'} scored
                </h3>
              </div>
              {batch.count === 0 ? (
                <p className="text-sm text-cn-text-muted">No draft insurance claims to score.</p>
              ) : (
                <div className="space-y-2">
                  {batch.results.map((r) => (
                    <button
                      type="button"
                      key={r.claim_id}
                      onClick={() => openDetail(r.claim_id)}
                      className="w-full text-left flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-cn-border hover:border-cn-primary hover:bg-cn-bone-100/50 transition-colors"
                      data-testid={`denial-batch-row-${r.claim_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-cn-text dark:text-white truncate">
                          {r.claim_number} · {r.patient_name}
                        </div>
                        <div className="text-xs text-cn-text-muted truncate">
                          {r.primary_payer || 'Unknown payer'} · {money(r.total_charges)}
                          {r.top_issue && <> · <span className="text-amber-600">{r.top_issue}</span></>}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${bandStyle(r.band)}`}>
                        {Math.round((r.probability || 0) * 100)}% · {r.band}
                      </span>
                      <ChevronRight className="w-4 h-4 text-cn-text-muted shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Detail panel */}
          {activeClaim && (
            <div className="cn-card p-6 mt-6" data-testid="denial-detail-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">
                  Prediction detail
                </h3>
                <button
                  type="button"
                  onClick={() => { setActiveClaim(null); setActiveDetail(null); }}
                  className="text-xs text-cn-text-muted hover:text-cn-text"
                  data-testid="denial-detail-close"
                >
                  Close
                </button>
              </div>

              {detailLoading && (
                <div className="flex items-center gap-2 text-sm text-cn-text-muted py-8">
                  <Loader2 className="w-4 h-4 animate-spin" /> Running prediction…
                </div>
              )}

              {activeDetail && (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                    <Gauge probability={activeDetail.probability} />
                    <div className="text-xs text-cn-text-muted space-y-0.5">
                      <div>Scrub score: <span className="font-bold">{Math.round((activeDetail.components?.scrub_probability || 0) * 100)}%</span></div>
                      <div>Payer historical: <span className="font-bold">{Math.round((activeDetail.components?.payer_historical || 0) * 100)}%</span></div>
                      <div>History confidence: <span className="font-bold">{Math.round((activeDetail.components?.history_confidence || 0) * 100)}%</span> ({activeDetail.components?.n_prior_claims || 0} priors)</div>
                      {activeDetail.ai_unavailable && (
                        <div className="text-amber-600">AI offline — deterministic checks only.</div>
                      )}
                    </div>
                  </div>

                  {activeDetail.suggested_edits?.length > 0 && (
                    <div data-testid="denial-suggested-edits">
                      <h4 className="text-sm font-semibold text-cn-text dark:text-white mb-2">Suggested edits</h4>
                      <ul className="space-y-2">
                        {activeDetail.suggested_edits.map((s, i) => (
                          <li
                            key={i}
                            className={`p-3 rounded-lg border text-xs ${s.severity === 'blocker' ? 'bg-red-50/60 border-red-200' : 'bg-amber-50/60 border-amber-200'}`}
                          >
                            <div className="font-semibold text-cn-text dark:text-white">{s.issue}</div>
                            <div className="text-cn-text-muted mt-1">→ {s.fix}</div>
                            {s.rule && <div className="text-[10px] text-cn-text-muted/80 mt-1 font-mono">{s.rule}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {activeDetail.verified_clean?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-cn-text dark:text-white mb-2">Verified clean</h4>
                      <ul className="text-xs text-cn-text-muted space-y-1">
                        {activeDetail.verified_clean.map((v, i) => (
                          <li key={i}>✓ {v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!batch && (
            <div className="cn-card p-8 mt-6 text-center text-sm text-cn-text-muted" data-testid="denial-batch-empty">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 opacity-40" />
              Click &quot;Scan all draft claims&quot; to score every unsubmitted insurance claim.
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}
